/**
 * This service uses stripe SDK
 * https://stripe.com/docs/terminal/references/api/js-sdk#stripeterminal-create
 */
import {
  loadStripeTerminal,
  type StripeTerminal,
  type Terminal,
  type Reader,
  type ISdkManagedPaymentIntent,
} from '@stripe/terminal-js'
import type { ComputedRef } from 'vue'
import { computed, ref, reactive, watch } from 'vue'

interface CreateStripeSdkParams {
  getConnectionToken: () => Promise<string>
  handleUnexpectedTerminalDisconnect: () => void
}
interface SimulatorConfig {
  simulated: boolean | null
  testCardNumber: string | null
}

const SIMULATOR = 'SIMULATOR'
const terminal = ref<Terminal | null>(null)

export async function createStripeSdk ({
  getConnectionToken,
  handleUnexpectedTerminalDisconnect,
}: CreateStripeSdkParams): Promise<{
  connect: (reader: Reader) => Promise<unknown>
  disconnect: () => Promise<void>
  discoverReaders: (data: { simulated?: boolean }) => Promise<unknown>
  collectPaymentMethod: (clientSecret: string) => Promise<unknown>
  processPayment: (paymentIntent: ISdkManagedPaymentIntent) => Promise<unknown>
  cancelCollectPaymentMethod: () => Promise<void>
  destroyService: () => void
  paymentStatus: ComputedRef<unknown>
  connectionStatus: ComputedRef<string | undefined>
  simulatorConfig: SimulatorConfig
  wifiCheckoutPending: { value: boolean | null }
  setSimulatedTerminal: (opts?: { config?: SimulatorConfig; reader?: Reader }) => void
  unwatchSimulatorConfig?: (() => void) | null
}> {
  if (terminal?.value) throw new Error('sdk was already called')

  const StripeTerminal: StripeTerminal = await loadStripeTerminal()

  terminal.value = StripeTerminal.create({
    onFetchConnectionToken: getConnectionToken,
    onUnexpectedReaderDisconnect: handleUnexpectedTerminalDisconnect,
  }) as unknown as Terminal

  const wifiCheckoutPending = ref(false)
  let unwatchSimulatorConfig: (() => void) | null = null

  const simulatorConfig = reactive<SimulatorConfig>({
    simulated: false,
    testCardNumber: null,
  })

  unwatchSimulatorConfig = watch(() => simulatorConfig, (conf) => {
    setSimulatedTerminal({ config: conf })
  }, { deep: true })

  // I still recommend extracting SDK methods, we should avoid using that directly if possible
  function setSimulatedTerminal ({ config, reader }: { config?: SimulatorConfig; reader?: Reader } = {}): void {
    const { simulated, testCardNumber } = config || simulatorConfig
    if (simulated && testCardNumber) {
      terminal?.value?.setSimulatorConfiguration({ testCardNumber })
    }
  }

  async function connect (reader: Reader): Promise<unknown> {
    const connectResult = await terminal?.value?.connectReader(reader)

    const { simulated, testCardNumber } = simulatorConfig
    if (reader.id === SIMULATOR && simulated && testCardNumber) {
      await terminal?.value?.setSimulatorConfiguration({ testCardNumber })
    }

    return connectResult
  }

  async function disconnect (): Promise<void> {
    await terminal?.value?.disconnectReader()
  }

  async function discoverReaders (data: { simulated?: boolean }): Promise<unknown> {
    return terminal?.value?.discoverReaders(data)
  }

  const paymentStatus = computed(() => terminal?.value?.getPaymentStatus())

  const connectionStatus = computed(() => terminal?.value?.getConnectionStatus())

  async function collectPaymentMethod (clientSecret: string): Promise<unknown> {
    wifiCheckoutPending.value = true
    const collectResult = await terminal?.value?.collectPaymentMethod(clientSecret) as { error?: unknown }

    if (collectResult?.error) throw collectResult.error

    return collectResult
  }

  async function processPayment (paymentIntent: ISdkManagedPaymentIntent): Promise<unknown> {
    wifiCheckoutPending.value = false
    const processResult = await terminal?.value?.processPayment(paymentIntent) as { error?: unknown }

    if (processResult?.error) throw processResult.error

    return processResult
  }

  async function cancelCollectPaymentMethod (): Promise<void> {
    await terminal?.value?.cancelCollectPaymentMethod()
  }

  function destroyService (): void {
    terminal.value = null
    wifiCheckoutPending.value = null
    simulatorConfig.simulated = null
    simulatorConfig.testCardNumber = null
    unwatchSimulatorConfig?.()
  }

  return {
    connect,
    disconnect,
    discoverReaders,
    collectPaymentMethod,
    processPayment,
    cancelCollectPaymentMethod,
    destroyService,
    paymentStatus,
    connectionStatus,
    simulatorConfig,
    wifiCheckoutPending,
    setSimulatedTerminal,
    unwatchSimulatorConfig,
  }
}
